import { useEffect, type ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";
import { PublicShareAvailabilityDocument } from "@/graphql/generated/public";
import { publicClient } from "@/graphql/public-client";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { loginUrlFor } from "@/auth/login-url";
import { getConfig } from "@/lib/config";
import { userErrorMessage } from "@/lib/user-error";

export function PublicShareAvailability({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const location = useLocation();
  const { data, loading, error, refetch } = useQuery(PublicShareAvailabilityDocument, { client: publicClient });
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") void refetch(); };
    document.addEventListener("visibilitychange", refresh);
    return () => document.removeEventListener("visibilitychange", refresh);
  }, [refetch]);
  if (!error && data?.publicShareEnabled === true) return children;
  const next = `${getConfig().basePath.replace(/\/$/, "")}${location.pathname}${location.search}`;
  return <main id="public-share-availability" className="mx-auto max-w-2xl space-y-5 px-6 py-12">
    {loading ? <p role="status"><FormattedMessage id="common.loading" /></p> : <>
      <p role={error ? "alert" : undefined} className="text-smycles-body text-ink">
        {error ? userErrorMessage(intl, error) : <FormattedMessage id="share.public.disabled" />}
      </p>
      {error && <Button id="public-share-availability-retry" onClick={() => void refetch()}><FormattedMessage id="share.public.retry" /></Button>}
      <a id="public-share-availability-login" href={loginUrlFor(next)} className={buttonClasses({ variant: "primary" })}>
        <FormattedMessage id="share.public.login" />
      </a>
    </>}
  </main>;
}
